import infoIcon from 'assets/images/icons/info.png';
import { useTranslation } from 'translation/useTranslation';
import sanitize from 'helpers/sanitize';
import { useEffect, useMemo, useState } from 'react';
import { empty, formatNumberTo } from 'helpers/common';
import EditorInput from 'components/ui/EditorInput';
import ExpandableHtml from 'components/ui/ExpandableHtml';
import { Button, Input, InputAffix } from 'components/ui';

function V2SystematicObectiveRow({ no, data, hasSub, settings, balancedScorecardDetails, handleLevelChange, readOnlyMode, levels, countFulfillment, useGeneral, extremeLevels, weighted, errorField = null, setErrorField = null }) {
  const { editableTranslate } = useTranslation();

  const [weight, setWeight] = useState('');
  const [isInvalidWeight, setIsInvalidWeight] = useState(false);

  const [showComment, setShowComment] = useState(false);

  const savedSystematicObjective = useMemo(() => {
    if (balancedScorecardDetails?.agreement_data?.systematicObjectives && Array.isArray(balancedScorecardDetails?.agreement_data?.systematicObjectives)) {
      const filtered = balancedScorecardDetails.agreement_data.systematicObjectives.filter(o => o.name === data.systematicObjectiveGridName);

      if (filtered.length > 0) {
        let agreed = filtered[0].agreed;

        if (agreed) return filtered[0];
      }
    }
    return null;
  }, [balancedScorecardDetails]);

  useEffect(() => {
    if (savedSystematicObjective) {
      setWeight(savedSystematicObjective?.weight || '');
    }
  }, [savedSystematicObjective]);

  const handleWeightChange = (inputValue, obj) => {
    handleLevelChange(inputValue, 'systematic', obj, 'weight');
  };

  useEffect(() => {
    const temp = Number(formatNumberTo(weight, '.'));
    if (!empty(weight) && temp >= 0 && temp <= 100) {
      setIsInvalidWeight(false);
      if (savedSystematicObjective && weight !== savedSystematicObjective?.weight && !weight.endsWith('.') && !weight.endsWith(',')) {
        handleWeightChange(weight, savedSystematicObjective);
      }
    } else {
      setIsInvalidWeight(true);
    }
  }, [weight]);

  useEffect(() => {
    if (errorField?.systematic?.totalWeight) setIsInvalidWeight(true);
    else setIsInvalidWeight(false);
  }, [weight, errorField]);

  let count = 0;

  return savedSystematicObjective && <>
    <tr className={`${hasSub ? 'tr-criteria' : ''} ${errorField?.systematic?.objective?.name === data.systematicObjectiveGridName ? 'invalid-tr' : ''}`}>
      {
        weighted && <td className={`col-weight ${isInvalidWeight ? 'has-invalid' : ''}`}>
          <InputAffix suffix="%" className="peb-input-affix--sm" error={isInvalidWeight}>
            <Input data-testid="weight-input" type="text" className="text-right" error={isInvalidWeight} maxLength={6} value={weight} onChange={e => setWeight(e.target.value)} readOnly={readOnlyMode} />
          </InputAffix>
        </td>
      }
      <td className="text-center w-1px">{no}</td>
      <td colSpan={hasSub ? 2 : 1} className={hasSub ? 'pl-3 td-sub no-border-right' : 'no-sub'}>{data.systematicObjectiveGridName}</td>
      {
        levels.map((item, i) => <td key={i} className={`text-center col-radio ${i > 0 && i < levels.length - 1 ? 'bg-white' : 'col-not-fulfillment'}`}>
          {
            !hasSub && <input type="checkbox" className="mx-auto" name={data.systematicObjectiveGridName} value={item.level} disabled={readOnlyMode} onChange={(e) => handleLevelChange(e, 'systematic', { name: data.systematicObjectiveGridName })} checked={item.level == savedSystematicObjective?.level} data-testid={`systematic-objective-level-${item.level}`} />
          }
        </td>)
      }
      <td className={`v2-comment-td text-center ${showComment ? 'opened' : 'w-1px'}`}>
        {
          !hasSub && <V2LevelComment showComment={showComment} setShowComment={setShowComment} useGeneral={useGeneral} settings={settings} data={savedSystematicObjective} extremeLevels={extremeLevels} readOnlyMode={readOnlyMode} handleSave={v => handleLevelChange(v, 'systematic', savedSystematicObjective, 'comment')} />
        }
      </td>
    </tr>
  </>
}

function V2SystematicObjective({ systematicComment, setSystematicComment, showSystematicComment, setShowSystematicComment, systematicResultCommentMandatory, handleSaveResultComment, settings, balancedScorecardDetails, handleLevelChange, readOnlyMode, levels, countSystematic, useGeneral, extremeLevels, weighted, errorField = null, setErrorField = null }) {
  const { translate, editableTranslate } = useTranslation();

  const [sortedLevels, setSortedLevels] = useState([]);
  const [countFulfillment, setCountFulfillment] = useState(0);

  const [systematicCommentError, setSystematicCommentError] = useState(false);

  useEffect(() => {
    const temp = structuredClone(levels) || [];
    temp.sort((a, b) => {
      const aVal = a.fulfillment === true ? 1 : 0;
      const bVal = b.fulfillment === true ? 1 : 0;
      return bVal - aVal;
    });

    setCountFulfillment(temp.filter(t => t.fulfillment === true).length);
    setSortedLevels(temp);
  }, [levels]);

  useEffect(() => {
    setSystematicCommentError(false);
  }, [showSystematicComment]);

  const handleSaveComment = () => {
    if (systematicCommentError) return;

    handleSaveResultComment('systematic', systematicComment, setShowSystematicComment)
  };

  return <div className={`mt-4 custom-systematic-div ${settings?.shouldHasSubCriteria === true ? 'has-sub' : ''}`}>
    <table className="table table-borderless" data-testid="systematic-objective-datagrid-table">
      <thead>
        {/* <tr className="custom-th-info">
          <th colSpan={settings?.shouldHasSubCriteria === true ? (weighted ? 4 : 3) : (weighted ? 3 : 2)}></th>
          {
            countFulfillment > 0 && <th colSpan={countFulfillment} className={`th-info border-right text-center`} dangerouslySetInnerHTML={{ __html: translate('evaluation.expectations_met', 'Erwartungen erfüllt').replaceAll(' ', countFulfillment < 3 ? '<br />' : ' ') }} />
          }
          {
            countFulfillment !== sortedLevels.length && <th colSpan={sortedLevels.length - countFulfillment} className={`th-info text-center`} dangerouslySetInnerHTML={{ __html: translate('evaluation.expectations_significantly_exceeded', 'Erwartungen deutlich übertroffen').replaceAll(' ', sortedLevels.length - countFulfillment < 3 ? '<br />' : ' ') }} />
          }
          <th></th>
        </tr> */}
        <tr className="custom-th">
          {
            weighted && <th className="col-weight"></th>
          }
          <th className="text-center w-1px">{editableTranslate('evaluation.table_row_no')}</th>
          <th colSpan={settings?.shouldHasSubCriteria === true ? 2 : 1} className={settings?.shouldHasSubCriteria === true ? 'no-border-right' : ''}>{editableTranslate('evaluation.evaluation_criteria')}</th>
          {
            sortedLevels.map((item, i) => <th key={i} className={`text-center col-radio level-th ${weighted ? 'has-weight' : ''}`}>{item.level}</th>)
          }
          <th className="text-center w-1px">{editableTranslate('evaluation.reason')}</th>
        </tr>
      </thead>
      <tbody>
        {
          settings?.shouldHasSubCriteria === false && settings?.systematicObjectiveDatagrid && settings?.systematicObjectiveDatagrid.map((item, i) => <V2SystematicObectiveRow key={i} no={i + 1} data={item} settings={settings} balancedScorecardDetails={balancedScorecardDetails} handleLevelChange={handleLevelChange} readOnlyMode={readOnlyMode} levels={sortedLevels} countFulfillment={countFulfillment} useGeneral={useGeneral} extremeLevels={extremeLevels} weighted={weighted} errorField={errorField} setErrorField={setErrorField} />)
        }
        {
          settings?.shouldHasSubCriteria === true && settings?.systematicObjectiveDatagrid_subCriteria && settings?.systematicObjectiveDatagrid_subCriteria.map((item, i) => <V2SystematicObectiveRow key={i} no={i + 1} data={item} hasSub={true} settings={settings} balancedScorecardDetails={balancedScorecardDetails} handleLevelChange={handleLevelChange} readOnlyMode={readOnlyMode} levels={sortedLevels} countFulfillment={countFulfillment} useGeneral={useGeneral} extremeLevels={extremeLevels} weighted={weighted} errorField={errorField} setErrorField={setErrorField} />)
        }
        {
          countSystematic === 0 && <tr className="no-height">
            <td colSpan={sortedLevels.length + (settings?.shouldHasSubCriteria === true ? 5 : 4)} className="text-center">
              {editableTranslate('evaluation.no_systematic_objectives_in_lk')}
            </td>
          </tr>
        }
        <tr className="no-height">
          <td colSpan={sortedLevels.length + 2 + (weighted ? 1 : 0)} className="no-border-right pr-0 dark-bg">
            <div className="text-right font-weight-bold">{editableTranslate('balanced_scorecard.overall_result')}:</div>
          </td>
          {/* <td colSpan={sortedLevels.length + (settings?.shouldHasSubCriteria === true ? (weighted ? 4 : 3) : (weighted ? 3 : 2))} className="text-right font-weight-bold no-border-right pr-0">{editableTranslate('evaluation.systematic_v2_total_performance_rating')}:</td> */}
          <td className="font-weight-bold no-border-left v-top dark-bg position-relative">{balancedScorecardDetails?.agreement_data?.totalValue?.systematic}
            {
              !showSystematicComment && (
                systematicResultCommentMandatory ?
                  <Button variant="ghost" className="position-absolute pl-2 top-2" data-tooltip-id="extreme-comment-tooltip" onClick={() => setShowSystematicComment(!showSystematicComment)} data-testid="comment-toggle-systematic">
                    <i className="bi bi-exclamation-circle-fill text-normal"></i>
                  </Button> :
                  <Button variant="ghost" className="position-absolute pl-2 top-2" data-tooltip-id="leave-comment-tooltip" onClick={() => setShowSystematicComment(!showSystematicComment)} data-testid="comment-toggle-systematic">
                    <i className="bi bi-chat-left-text text-normal"></i>
                  </Button>
              )
            }
          </td>
        </tr>
        {
          (settings?.shouldConvertResultToPayoutPoints && balancedScorecardDetails?.agreement_data?.payout_points) && <tr className="no-height">
            <td colSpan={sortedLevels.length + 2 + (weighted ? 1 : 0)} className="no-border-right pr-0 dark-bg">
              <div className="text-right font-weight-bold">{editableTranslate('balanced_scorecard.payout_points')}:</div>
            </td>
            <td className="font-weight-bold no-border-left v-top dark-bg">
              {balancedScorecardDetails?.agreement_data?.payout_points}
            </td>
          </tr>
        }
        {
          showSystematicComment && <tr>
            <td colSpan={sortedLevels.length + 3 + (weighted ? 1 : 0)} className="p-0 bg-white">
              <div className="result-comment-div pt-0 position-relative">
                <EditorInput data-testid="systematic-comment-input"
                  className={`editor-no-border bg-white ${readOnlyMode ? 'hide-toolbar' : ''}`}
                  value={systematicComment}
                  onChangeCallback={(v) => setSystematicComment(v)}
                  validate={(v) => v ? setSystematicCommentError(true) : setSystematicCommentError(false)}
                  readOnly={readOnlyMode} />
                {
                  !readOnlyMode && <Button onClick={handleSaveComment} variant="chip-success" icon="bi-check" className="btn-save-level-comment" data-testid="confirm-button" />
                }
                <Button onClick={() => setShowSystematicComment(false)} variant="chip-accent" icon="bi-x" className="btn-cancel-level-comment" data-testid="cancel-button" />
              </div>
            </td>
          </tr>
        }
      </tbody>
    </table>
  </div>
}

function V2LevelComment({ showComment, setShowComment, useGeneral = false, settings, data, extremeLevels, readOnlyMode, handleSave }) {
  const { editableTranslate } = useTranslation();

  const isMandatory = useMemo(() => {
    if (useGeneral) return settings?.individualMandatoryCommentForExtremeRating === true && settings?.individualMandatoryCommentForExtremeRatingType === 'based_on_single_objective' && (data?.level && extremeLevels.individual.includes(data.level));

    return settings?.systematicMandatoryCommentForExtremeRating === true && settings?.systematicMandatoryCommentForExtremeRatingType === 'based_on_single_objective' && (data?.level && extremeLevels.systematic.includes(data.level));
  }, [settings, data, extremeLevels]);

  const [comment, setComment] = useState(data?.comment || null);

  const [commentError, setCommentError] = useState(false);

  const handleSaveComment = () => {
    if (comment && !commentError) {
      handleSave(comment);
      setShowComment(false);
    }
  };

  useEffect(() => {
    setCommentError(false);
  }, [showComment]);

  return <>
    {
      showComment && <div className="v2-level-comment-div position-relative">
        <EditorInput data-testid="comment-input"
          className={`editor-no-border has-top ${readOnlyMode ? 'hide-toolbar' : ''}`}
          value={comment}
          onChangeCallback={(v) => setComment(v)}
          validate={(v) => v ? setCommentError(true) : setCommentError(false)}
          readOnly={readOnlyMode} />
        {
          !readOnlyMode && <Button onClick={handleSaveComment} variant="chip-success" icon="bi-check" className="btn-save-level-comment" data-testid="confirm-button" />
        }
        <Button onClick={() => setShowComment(false)} variant="chip-accent" icon="bi-x" className="btn-cancel-level-comment" data-testid="cancel-button" />
      </div>
    }
    {
      !showComment && (
        isMandatory ?
          <Button variant="ghost" size="inline" icon="bi-exclamation-circle-fill" data-tooltip-id="extreme-comment-tooltip" onClick={() => setShowComment(true)} data-testid="comment-toggle" /> :
          <Button variant="ghost" size="inline" icon="bi-chat-left-text" data-tooltip-id="leave-comment-tooltip" onClick={() => setShowComment(true)} data-testid="comment-toggle" />
      )
    }
  </>
}

export default function Evaluation({ settings, balancedScorecardDetails, handleLevelChange, readOnlyMode, levels, countSystematic, useGeneral, extremeLevels, weight, weighted, bonus, handleBonusChange, handleSaveBonus, systematicResultCommentMandatory, handleSaveResultComment, systematicComment, setSystematicComment, showSystematicComment, setShowSystematicComment, errorField = null, setErrorField = null }) {
  const { translate, editableTranslate } = useTranslation();

  return (
    <div>
      {
        settings?.showRatingNotes && settings?.ratingNotes &&
        <div className="feedback-div d-flex mt-4">
          <div className="mr-2">
            <img src={infoIcon} alt="info" />
          </div>
          <div className="w-100">
            <ExpandableHtml html={sanitize(settings?.ratingNotes)} className="fade-blue" />
          </div>
        </div>
      }

      <V2SystematicObjective systematicComment={systematicComment} setSystematicComment={setSystematicComment} showSystematicComment={showSystematicComment} setShowSystematicComment={setShowSystematicComment} systematicResultCommentMandatory={systematicResultCommentMandatory} handleSaveResultComment={handleSaveResultComment} settings={settings} balancedScorecardDetails={balancedScorecardDetails} handleLevelChange={handleLevelChange} readOnlyMode={readOnlyMode} levels={levels?.general ?? levels?.systematic} countSystematic={countSystematic} useGeneral={useGeneral} extremeLevels={extremeLevels} weighted={weighted} errorField={errorField} setErrorField={setErrorField} />
    </div>
  );
}